import type { BADGE_FALLBACK } from '@uefadigital/panenka';
import { PkAvatar, PkBadge, PkCard, PkIdentifier } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useMemo } from 'react';

import type { PlayerItem } from './types';

import { usePlayerLinkRule } from '@hooks/useLinkRule';
import { useWindowSize } from '@hooks/useWindowSize';
import { trackCardElement } from '@services/GoogleTagManagerService';
import { generateRandomNumber } from '@utilities/random';
import { competitionCode } from '@utilities/WindowContext';

import * as styles from './styles.module.scss';

const PlayerCard = ({
  player: { id, shortName, fieldPosition, imageUrl, team, nationalTeam, context },
}: {
  player: PlayerItem;
}) => {
  const { isMobile } = useWindowSize();

  const trackingData = useMemo(
    () => ({
      id,
      name: 'Related',
      category: 'related player',
      brand: competitionCode,
    }),
    [id],
  );

  const link = usePlayerLinkRule(id, context?.at(0).competition?.id);

  return (
    <a key={id} href={link} className={styles.link} ref={trackCardElement(trackingData)}>
      <PkCard className={clsx(styles.card, styles.cardWithHover)}>
        <div slot='body' className={styles.content}>
          <PkAvatar
            className='pk-d--flex pk-justify-content--center pk-mb--s'
            type='image'
            size={isMobile ? 88 : 120}
            fallbackImage='generic-head'
            src={imageUrl}
          />
          <div className={clsx(styles.name, 'pk-text--center')}>{shortName}</div>
          <div className={clsx(styles.position, 'pk-text--center')}>{fieldPosition}</div>
          {isMobile && (
            <div className={clsx(styles.teams, 'pk-d--flex pk-justify-content--center')}>
              {team && (
                <PkBadge alt={team.displayName} src={team.bigLogoUrl} fallbackImage='club-generic-badge' size={24} />
              )}
              {nationalTeam && (
                <PkBadge
                  alt={nationalTeam.teamCode}
                  src={nationalTeam.bigLogoUrl}
                  fallbackImage={`ntc-generic-badge-0${generateRandomNumber(1, 4)}` as BADGE_FALLBACK}
                  size={24}
                />
              )}
            </div>
          )}
          {!isMobile && (
            <div className={styles.details}>
              {team && (
                <PkIdentifier type='vertical' alignment='center' className='pk-p--0'>
                  <span slot='prefix' className='pk-mb--xs2'>
                    <PkBadge
                      alt={team.displayName}
                      src={team.bigLogoUrl}
                      fallbackImage='club-generic-badge'
                      size={24}
                    />
                  </span>
                  <span slot='secondary' className='pk-text--text-02 pk-font-size--s'>
                    {team.displayName}
                  </span>
                </PkIdentifier>
              )}
              {nationalTeam && (
                <PkIdentifier type='vertical' alignment='center' className='pk-p--0'>
                  <span slot='prefix' className='pk-mb--xs2'>
                    <PkBadge
                      alt={nationalTeam.teamCode}
                      src={nationalTeam.bigLogoUrl}
                      fallbackImage={`ntc-generic-badge-0${generateRandomNumber(1, 4)}` as BADGE_FALLBACK}
                      size={24}
                    />
                  </span>
                  <span slot='secondary' className='pk-text--text-02 pk-font-size--s'>
                    {nationalTeam.teamCode}
                  </span>
                </PkIdentifier>
              )}
            </div>
          )}
        </div>
      </PkCard>
    </a>
  );
};

export default memo(PlayerCard);
